import { useDispatch, useSelector } from "react-redux"
import { setFilters } from "../../../filters/actions"
import { getFilters } from "../../../filters/selectors"
import { getBusinessStartOfWeekDay, getMidnightOffset } from "../../settings/selectors"
import { REPORTS_MAX_RANGE_DAYS } from "../utils"
import { StateObject } from "../../../redux/types"
import type { FiltersIdentifier } from "../../../filters/types"
import { DateRangePicker } from "../../../uiKit/DateRangePicker/DateRangePicker"
import { DateRangePickerValue } from "../../../uiKit/DateRangePicker/types"
import { getIsoDateNow } from "../../../uiKit/DateRangePicker/utils"
import { IsoDate, IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { getUtcEndOfDay, getUtcStartOfDay } from "../../../../utils/dateFunctions"
import * as React from "react"

type Props = {
    identifier: FiltersIdentifier
    maxDaysRange?: number
    className?: string
    style?: React.CSSProperties
}
export const DateRangePickerDataWrapper = ({ maxDaysRange = REPORTS_MAX_RANGE_DAYS, ...props }: Props) => {
    const dispatch = useDispatch()

    const filters = useSelector((state: StateObject) => getFilters(state, props.identifier))
    const midnightOffset = useSelector(getMidnightOffset)
    const weekStartDay = useSelector(getBusinessStartOfWeekDay)

    // Old endpoints are working with IsoDateTime format (e.g. "2022-01-01T00:00:00Z"),
    // so we need to convert IsoDate to IsoDateTime, apply start/end of day time, and apply midnightOffset
    const getIsoDateTimeStart = (startIsoDate: IsoDate): IsoDateTime => {
        return getUtcStartOfDay(startIsoDate, null, midnightOffset)
    }
    const getIsoDateTimeEnd = (endIsoDate: IsoDate): IsoDateTime => {
        return getUtcEndOfDay(endIsoDate, null, midnightOffset)
    }

    const onChange = (value: DateRangePickerValue) => {
        const { start, end } = value

        dispatch(
            setFilters(props.identifier, {
                // IsoDateTime format for old Java endpoints
                timestamp: {
                    from: start ? getIsoDateTimeStart(start) : null,
                    to: end ? getIsoDateTimeEnd(end) : null,
                },
                date: {
                    from: start ?? null,
                    to: end ?? null,
                },
            })
        )
    }

    return (
        <div className={props.className} style={props.style}>
            <DateRangePicker
                minDate="2012-01-01"
                maxDate={getIsoDateNow()}
                maxRange={maxDaysRange}
                weekStartDay={weekStartDay}
                value={{
                    start: filters.date?.from ?? null,
                    end: filters.date?.to ?? null,
                }}
                onChange={onChange}
            />
        </div>
    )
}
